import { FC, useState } from 'react';
import { Alert, Svg } from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import { colorPalette, colorTokens } from '@rideiq/shared/components/variables';
import { useToastStore } from '@rideiq/shared/stores/toastStore';

import {
  BannerContainer,
  BannerDescription,
  BannerTitle,
  CloseButton,
  CloseButtonContainer,
  SearchContainer,
  TextColumn,
} from './SisImportBanner.styles';
import { SisStudentSearch } from './SisStudentSearch';
import { SisRecord, SisStudentDetails } from './types';
import { useSisStudentSearch } from './useSisStudentSearch';

export type SisStatus = 'available' | 'no-records';

interface SisImportBannerProps {
  onImport: (studentData: SisStudentDetails) => void;
  sisStatus: SisStatus;
}

type BannerView = 'banner' | 'success' | 'hidden';

export const SisImportBanner: FC<SisImportBannerProps> = ({ onImport, sisStatus }) => {
  const [view, setView] = useState<BannerView>('banner');
  const [selectedStudent, setSelectedStudent] = useState<SisRecord | null>(null);
  const { getStudentById, isImporting, hasNoRecords } = useSisStudentSearch(sisStatus === 'no-records');
  const { addToast } = useToastStore();

  const handleImport = async () => {
    if (!selectedStudent) {
      return;
    }

    const studentDetails = await getStudentById(selectedStudent.id);

    if (studentDetails) {
      onImport(studentDetails);
      setView('success');
    } else {
      addToast({ severity: 'error', message: 'Failed to import student. Please try again.' });
    }
  };

  if (view === 'hidden') {
    return null;
  }

  if (view === 'success') {
    return (
      <Alert
        severity="success"
        title="Rider information imported!"
        message="Review and update as needed before saving."
        sx={{
          mb: '24px',
          backgroundColor: colorPalette.green95,
          color: colorPalette.green30,
          '& .MuiAlert-icon': { color: colorPalette.green30 },
        }}
      />
    );
  }

  if (hasNoRecords) {
    return (
      <Alert
        severity="error"
        title="Cannot import Rider information from SIS"
        message="No records found. Integration filters may be preventing data from being shared. Contact your IT team to check filter settings."
        sx={{
          mb: '24px',
          backgroundColor: colorPalette.red95,
          color: colorPalette.red20,
          '& .MuiAlert-icon': { color: colorPalette.red43 },
        }}
      />
    );
  }

  return (
    <BannerContainer>
      <TextColumn>
        <BannerTitle>Import rider information</BannerTitle>
        <BannerDescription>
          Search by name or ID to pull in rider details directly from your Student Information System (SIS)
        </BannerDescription>
        <SearchContainer>
          <SisStudentSearch onSelect={setSelectedStudent} selectedStudent={selectedStudent} />
          <Button color="secondary" size="large" onClick={handleImport} disabled={!selectedStudent || isImporting}>
            {isImporting ? 'Importing...' : 'Import'}
          </Button>
        </SearchContainer>
      </TextColumn>
      <CloseButtonContainer>
        <CloseButton onClick={() => setView('hidden')} aria-label="Close SIS import banner">
          <Svg icon="close" width="16px" color={colorTokens.text.contrast} />
        </CloseButton>
      </CloseButtonContainer>
    </BannerContainer>
  );
};

export * from './types';
